import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { trackEvent } from "../lib/tracking";
import api, { apiError } from "../lib/api";

const CartContext = createContext(null);

const STORAGE_KEY = "flonte_cart_v1";
const COUPON_KEY = "flonte_coupon_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [coupon, setCoupon] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(COUPON_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [bounce, setBounce] = useState(0);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore */
    }
  }, [items]);

  useEffect(() => {
    try {
      if (coupon) window.localStorage.setItem(COUPON_KEY, JSON.stringify(coupon));
      else window.localStorage.removeItem(COUPON_KEY);
    } catch {
      /* ignore */
    }
  }, [coupon]);

  const [taxPercent, setTaxPercent] = useState(0);
  const [shippingRule, setShippingRule] = useState({ freeAbove: 0, fee: 0 });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);
  useEffect(() => {
    api.get("/storefront/settings")
      .then(({ data }) => {
        const t = data?.store?.tax_percent;
        setTaxPercent(typeof t === "number" ? t : 0);
        setShippingRule({
          freeAbove: Number(data?.store?.free_shipping_above) || 0,
          fee: Number(data?.store?.shipping_fee) || 0,
        });
      })
      .catch(() => {
        setTaxPercent(0);
        setShippingRule({ freeAbove: 0, fee: 0 });
      });
  }, []);

  // Auto-apply a pending coupon (from exit-intent popup, hero CTA, etc) when items exist.
  useEffect(() => {
    if (coupon || !items.length) return;
    let pending = "";
    try { pending = window.localStorage.getItem("flonte_pending_coupon_v1") || ""; } catch { /* ignore */ }
    if (!pending) return;
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    api.post("/coupons/validate", { code: pending, subtotal })
      .then(({ data }) => {
        setCoupon(data);
        try { window.localStorage.removeItem("flonte_pending_coupon_v1"); } catch { /* ignore */ }
      })
      .catch(() => {});
  }, [items.length, coupon]);

  const addItem = (product, { size = "M", quantity = 1 } = {}) => {
    setItems((prev) => {
      const key = `${product.id}-${size}`;
      const existing = prev.find((x) => x.key === key);
      if (existing) {
        return prev.map((x) =>
          x.key === key ? { ...x, quantity: x.quantity + quantity } : x
        );
      }
      return [
        ...prev,
        {
          key,
          cartKey: key,
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.images?.[0],
          color: product.color,
          size,
          quantity,
        },
      ];
    });
    setBounce((b) => b + 1);
    setDrawerOpen(true);
    trackEvent("AddToCart", {
      value: product.price * quantity,
      currency: "INR",
      content_ids: [product.id],
      content_name: product.name,
    });
  };

  const updateQuantity = (key, quantity) => {
    setItems((prev) =>
      prev
        .map((x) => (x.key === key ? { ...x, quantity: Math.max(0, quantity) } : x))
        .filter((x) => x.quantity > 0)
    );
  };

  const removeItem = (key) => {
    setItems((prev) => prev.filter((x) => x.key !== key));
  };

  const clear = () => {
    setItems([]);
    setCoupon(null);
  };

  const applyCoupon = async (code) => {
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    try {
      const { data } = await api.post("/coupons/validate", { code, subtotal });
      setCoupon(data);
      return { ok: true, ...data };
    } catch (err) {
      setCoupon(null);
      return { ok: false, error: apiError(err) };
    }
  };

  const removeCoupon = () => setCoupon(null);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    // Recompute discount from current subtotal so coupon stays valid
    let discount = 0;
    if (coupon && subtotal >= 0) {
      if (coupon.type === "percent") {
        discount = Math.round((subtotal * coupon.value) / 100);
      } else {
        discount = Math.min(coupon.value, subtotal);
      }
    }
    // Shipping and tax are both computed off the pre-discount subtotal,
    // matching OrderPricingService::calcTotals() server-side exactly —
    // the coupon discount is subtracted only once, from the final total,
    // same as there. (Previously this hardcoded shipping to 0 no matter
    // what, so an order under the free-shipping threshold showed one
    // total here but was actually charged ₹99 more server-side.)
    const shipping = shippingRule.freeAbove > 0 && subtotal > 0 && subtotal < shippingRule.freeAbove
      ? shippingRule.fee
      : 0;
    const tax = Math.round((subtotal * (Number(taxPercent) || 0)) / 100);
    const total = Math.max(0, subtotal - discount + shipping + tax);
    const count = items.reduce((s, i) => s + i.quantity, 0);
    return { subtotal, discount, shipping, tax, total, count };
  }, [items, coupon, taxPercent, shippingRule]);

  const value = {
    items,
    addItem,
    updateQuantity,
    removeItem,
    clear,
    totals,
    bounce,
    coupon,
    applyCoupon,
    removeCoupon,
    drawerOpen,
    openDrawer,
    closeDrawer,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}

export function formatINR(value) {
  return `₹${value.toLocaleString("en-IN")}`;
}
